import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

interface PlatformConnection {
  platform: string;
  is_connected: boolean;
  site_url?: string;
  display_name?: string;
}

const API_BASE = import.meta.env.VITE_API_URL || '';

export function PlatformConnections() {
  const { getAuthToken } = useAuth();
  const [connections, setConnections] = useState<PlatformConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeForm, setActiveForm] = useState<string | null>(null);

  // WordPress form state
  const [wpSiteUrl, setWpSiteUrl] = useState('');
  const [wpUsername, setWpUsername] = useState('');
  const [wpAppPassword, setWpAppPassword] = useState('');

  // Ghost form state
  const [ghostSiteUrl, setGhostSiteUrl] = useState('');
  const [ghostApiKey, setGhostApiKey] = useState('');

  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function getHeaders() {
    const token = await getAuthToken();
    return { Authorization: `Bearer ${token}` };
  }

  useEffect(() => { fetchConnections(); }, []);

  async function fetchConnections() {
    try {
      const headers = await getHeaders();
      const { data } = await axios.get(`${API_BASE}/api/v1/platforms/connections`, { headers });
      setConnections(data);
    } catch (e) {
      console.error('Failed to load connections', e);
    } finally {
      setLoading(false);
    }
  }

  async function connectWordPress() {
    setConnecting(true); setError(null);
    try {
      const headers = await getHeaders();
      await axios.post(`${API_BASE}/api/v1/platforms/connect/wordpress`, {
        site_url: wpSiteUrl,
        username: wpUsername,
        application_password: wpAppPassword,
      }, { headers });
      setSuccess('WordPress connected successfully!');
      setActiveForm(null);
      setWpSiteUrl(''); setWpUsername(''); setWpAppPassword('');
      fetchConnections();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || 'Failed to connect WordPress');
    } finally {
      setConnecting(false);
    }
  }

  async function connectGhost() {
    setConnecting(true); setError(null);
    try {
      const headers = await getHeaders();
      await axios.post(`${API_BASE}/api/v1/platforms/connect/ghost`, {
        site_url: ghostSiteUrl,
        admin_api_key: ghostApiKey,
      }, { headers });
      setSuccess('Ghost connected successfully!');
      setActiveForm(null);
      setGhostSiteUrl(''); setGhostApiKey('');
      fetchConnections();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || 'Failed to connect Ghost');
    } finally {
      setConnecting(false);
    }
  }

  async function disconnect(platform: string) {
    if (!confirm(`Disconnect ${platform}? You'll need to reconnect to publish.`)) return;
    try {
      const headers = await getHeaders();
      await axios.delete(`${API_BASE}/api/v1/platforms/connections/${platform}`, { headers });
      setSuccess(`${platform} disconnected.`);
      fetchConnections();
    } catch (e) {
      setError('Failed to disconnect');
    }
  }

  function isConnected(platform: string) {
    return connections.find(c => c.platform === platform)?.is_connected ?? false;
  }

  function connectionInfo(platform: string) {
    return connections.find(c => c.platform === platform);
  }

  if (loading) return <div className="text-gray-500">Loading connections...</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">Connected Platforms</h2>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">{success}</div>}

      {/* WordPress */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900">WordPress (Self-Hosted)</h3>
            {isConnected('wordpress') ? (
              <>
                <p className="text-sm text-green-600">&#10003; Connected &mdash; {connectionInfo('wordpress')?.display_name}</p>
                {connectionInfo('wordpress')?.site_url && (
                  <p className="text-xs text-gray-400 mt-0.5">{connectionInfo('wordpress')?.site_url}</p>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-500">Not connected</p>
            )}
          </div>
          {isConnected('wordpress') ? (
            <button onClick={() => disconnect('wordpress')} className="text-sm text-red-600 hover:underline">Disconnect</button>
          ) : (
            <button onClick={() => setActiveForm(activeForm === 'wordpress' ? null : 'wordpress')}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
              Connect
            </button>
          )}
        </div>
        {activeForm === 'wordpress' && (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-gray-600">
              Go to your WordPress Admin &rarr; Users &rarr; Profile &rarr; Application Passwords to generate a password.
            </p>
            <input type="url" placeholder="https://yourblog.com" value={wpSiteUrl}
              onChange={e => setWpSiteUrl(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm" />
            <input type="text" placeholder="WordPress username" value={wpUsername}
              onChange={e => setWpUsername(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm" />
            <input type="password" placeholder="Application password (xxxx xxxx xxxx xxxx)" value={wpAppPassword}
              onChange={e => setWpAppPassword(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm" />
            <button onClick={connectWordPress} disabled={connecting || !wpSiteUrl || !wpUsername || !wpAppPassword}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
              {connecting ? 'Connecting...' : 'Connect WordPress'}
            </button>
          </div>
        )}
      </div>

      {/* Ghost */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900">Ghost</h3>
            {isConnected('ghost') ? (
              <p className="text-sm text-green-600">&#10003; Connected &mdash; {connectionInfo('ghost')?.display_name}</p>
            ) : (
              <p className="text-sm text-gray-500">Not connected</p>
            )}
          </div>
          {isConnected('ghost') ? (
            <button onClick={() => disconnect('ghost')} className="text-sm text-red-600 hover:underline">Disconnect</button>
          ) : (
            <button onClick={() => setActiveForm(activeForm === 'ghost' ? null : 'ghost')}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
              Connect
            </button>
          )}
        </div>
        {activeForm === 'ghost' && (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-gray-600">
              Go to Ghost Admin &rarr; Settings &rarr; Integrations &rarr; Add custom integration to get your Admin API key.
            </p>
            <input type="url" placeholder="https://yourblog.ghost.io" value={ghostSiteUrl}
              onChange={e => setGhostSiteUrl(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm" />
            <input type="text" placeholder="Admin API key (id:secret)" value={ghostApiKey}
              onChange={e => setGhostApiKey(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm" />
            <button onClick={connectGhost} disabled={connecting || !ghostSiteUrl || !ghostApiKey}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
              {connecting ? 'Connecting...' : 'Connect Ghost'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
